// In src/common/ask.js

import { resolveApiBase, request } from './api.js';
import { toggleCard, setCardPending, setPauseSelection, escapeHTML, showFilePickerDialog } from './ui.js';
import { getCommonBasePath, getRelativePath } from './utils/path-utils.js';

export function ensureAskModal() {
  let modal = document.getElementById("fc-ask-modal");
  if (modal) return modal;

  modal = document.createElement("div");
  modal.id = "fc-ask-modal";
  Object.assign(modal.style, {
    position: "fixed", inset: "0", background: "rgba(0,0,0,0.35)",
    display: "none", alignItems: "center", justifyContent: "center", zIndex: "9999",
  });

  const box = document.createElement("div");
  Object.assign(box.style, {
    width: "min(520px, 92vw)", background: "white", borderRadius: "12px",
    padding: "16px", boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
    fontFamily: "Segoe UI, system-ui, sans-serif",
  });

  const q = document.createElement("div");
  q.id = "fc-ask-q";
  Object.assign(q.style, { marginBottom: "12px", whiteSpace: "pre-wrap" });

  const input = document.createElement("input");
  input.id = "fc-ask-input";
  Object.assign(input.style, {
    width: "100%", padding: "10px 12px", border: "1px solid #ccc",
    borderRadius: "8px", outline: "none", marginBottom: "12px", fontSize: "14px",
  });
  input.addEventListener('focusin', () => setPauseSelection(true));
  input.addEventListener('focus', () => setPauseSelection(true));
  input.addEventListener('blur', () => setTimeout(() => setPauseSelection(false), 150));

  const row = document.createElement("div");
  Object.assign(row.style, { display: "flex", gap: "8px", justifyContent: "flex-end" });

  const cancel = document.createElement("button");
  cancel.textContent = "Cancel";
  Object.assign(cancel.style, {
    padding: "8px 12px", borderRadius: "8px", border: "1px solid #ddd",
    background: "#f6f6f6", cursor: "pointer",
  });

  const ok = document.createElement("button");
  ok.textContent = "OK";
  Object.assign(ok.style, {
    padding: "8px 12px", borderRadius: "8px", border: "1px solid #2563eb",
    background: "#2563eb", color: "white", cursor: "pointer",
  });

  row.append(cancel, ok);
  box.append(q, input, row);
  modal.append(box);
  document.body.append(modal);

  modal._show = (question) => {
    q.textContent = question || "Please provide an answer:";
    input.value = "";
    setPauseSelection(true);
    modal.style.display = "flex";
    setTimeout(() => input.focus(), 0);
  };
  modal._hide = () => { modal.style.display = "none"; setPauseSelection(false); };
  modal._input = input;
  modal._ok = ok;
  modal._cancel = cancel;

  return modal;
}

export function showResolveDialog(question) {
  return new Promise((resolve) => {
    const modal = ensureAskModal();
    const done = (val) => {
      modal._hide();
      modal._ok.onclick = null;
      modal._cancel.onclick = null;
      window.removeEventListener("keydown", onKey);
      resolve(val);
    };
    const onKey = (e) => {
      if (e.key === "Enter") done(modal._input.value.trim());
      else if (e.key === "Escape") done("N/A");
    };
    modal._ok.onclick = () => done(modal._input.value.trim());
    modal._cancel.onclick = () => done("N/A");
    window.addEventListener("keydown", onKey);

    modal._show(question);
  });
}

/**
 * Remove every card for a question that can no longer be answered (the server
 * retracted it, or /reply said it is unknown/expired), including its entry in
 * the verify-all overlay. The overlay API is read from its DOM node because
 * window.fcVerifyAllOverlay is cleared as soon as the run finishes.
 */
export function retractInlineAsk(qid) {
  if (!qid) return;
  document.querySelectorAll(`.fc-inline-ask[data-qid="${CSS.escape(qid)}"]`).forEach(el => el.remove());
  document.getElementById('verify-all-overlay')?.__api?.removeResolution(qid);
  setPauseSelection(false);
}

export function showInlineAsk(appEl, footnoteId, citeId, question, qid, clientId, documentId, apiBase = null, databaseManager = null, footnotesManager = null) {
  const card = appEl.querySelector(`.card[data-id="${CSS.escape(String(footnoteId))}"]`);
  if (!card) throw new Error(`Footnote card not found for id=${footnoteId}`);

  const header = card.querySelector('.card-header');
  if (header && header.getAttribute('aria-expanded') !== 'true') toggleCard(card);

  // Prefer ask-container (below verify button), fallback to results, then card-body
  const container = card.querySelector('.ask-container') || card.querySelector('.results') || card.querySelector('.card-body');
  if (!container) throw new Error(`No container (.ask-container|.results|.card-body) in card id=${footnoteId}`);

  let ask = container.querySelector(`.fc-inline-ask[data-qid="${CSS.escape(qid)}"]`);
  if (ask) {
    console.log(`[ask_user] Ask element already exists for question ${qid}, skipping`);
    return;
  }

  ask = document.createElement('div');
  ask.className = 'fc-inline-ask';
  ask.dataset.qid = qid;
  ask.dataset.citeId = citeId || '';
  ask.dataset.footnoteId = String(footnoteId);
  
  // Generate a unique ID for this ask instance
  const askId = `ask-${qid}-${Date.now()}`;
  ask.id = askId;
  console.log(`[ask_user] Creating new ask element with ID: ${askId} for footnote ${footnoteId}`);

  // Look up citation text from footnotesManager if available
  let citeText = null;
  if (citeId && footnotesManager) {
    try {
      // Method 1: Check if citation is accessible via a public method
      if (typeof footnotesManager.getCitationById === 'function') {
        const citation = footnotesManager.getCitationById(footnoteId, citeId);
        if (citation && citation.source && citation.source.raw) {
          citeText = citation.source.raw;
          console.log(`[ask_user] Found citation text via getCitationById:`, citeText.substring(0, 100));
        }
      }
      
      // Method 2: Fallback - check footnotesCache with cached_citations
      if (!citeText) {
        const footnote = footnotesManager.footnotesCache?.find(f => String(f.id) === String(footnoteId));
        if (footnote && footnote.cached_citations) {
          // Look for the citation with matching ID
          const citation = footnote.cached_citations.find(c => c.id === citeId);
          if (citation && citation.source && citation.source.raw) {
            citeText = citation.source.raw;
            console.log(`[ask_user] Found citation text from cache:`, citeText.substring(0, 100));
          }
        }
      }
    } catch (e) {
      console.error('[ask_user] Failed to look up citation text:', e);
      // Continue without citation text
    }
  }
  
  // Build citation label - always show if we have cite_id, with cite_text if available
  let citationLabel = '';
  if (citeId) {
    if (citeText) {
      // Show citation text (truncated if too long)
      const maxLength = 120;
      const truncated = citeText.length > maxLength ? citeText.substring(0, maxLength) + '...' : citeText;
      citationLabel = `<div class="citation-label"><strong>Citation ${citeId}:</strong> ${escapeHTML(truncated)}</div>`;
    } else {
      // Fallback to just showing citation number
      citationLabel = `<div class="citation-label">Citation ${citeId}</div>`;
    }
  }

  ask.innerHTML = `
    ${citationLabel}
    <div class="fc-inline-ask-question">${escapeHTML(question)}</div>

    <div class="fc-inline-ask-actions">
      <button class="pick-file btn-secondary" type="button">Choose file…</button>
      <button class="no-file btn-secondary" type="button">No file in DB</button>
      <button class="go-online btn-secondary" type="button">Go online</button>

      <span class="fc-inline-ask-chip hidden">
        Using: <strong class="chip-label"></strong>
        <button class="chip-clear" type="button" title="Clear">×</button>
      </span>
    </div>

    <div class="fc-inline-ask-input-row">
      <input class="free-input" type="text" placeholder="Optional note (e.g., 'Pièce 2' or 'First mémoire')" />
      <button class="ok btn-primary" type="button">Send</button>
      <button class="cancel btn-secondary" type="button">Dismiss</button>
    </div>
  `;

  const freeInput = ask.querySelector('.free-input');
  const ok = ask.querySelector('.ok');
  const cancel = ask.querySelector('.cancel');

  const pickBtn = ask.querySelector('.pick-file');
  const noFileBtn = ask.querySelector('.no-file');
  const goOnlineBtn = ask.querySelector('.go-online');

  const chip = ask.querySelector('.fc-inline-ask-chip');
  const chipLabel = ask.querySelector('.chip-label');
  const chipClear = ask.querySelector('.chip-clear');

  console.log('[ask_user] Buttons found:', { pickBtn, noFileBtn, goOnlineBtn });

  // Local state
  let selectedFileId = null;     // string
  let selectedFileLabel = null;  // string
  let mode = null;               // "file" | "no_file" | "go_online" | null

  const showChip = (label) => {
    chipLabel.textContent = label || '';
    chip.classList.remove('hidden');
  };
  const hideChip = () => {
    chip.classList.add('hidden');
    chipLabel.textContent = '';
  };

  const resetModes = () => {
    mode = null;
    selectedFileId = null;
    selectedFileLabel = null;
    hideChip();
    noFileBtn.classList.remove('active');
    goOnlineBtn.classList.remove('active');
  };

  const markActive = (btn) => {
    [noFileBtn, goOnlineBtn].forEach(b => b.classList.remove('active'));
    if (btn) btn.classList.add('active');
  };


  // Choose file flow
  pickBtn.addEventListener("click", async (e) => {
    try {
      // 1) Load files using DatabaseManager's loadFileList method
      let files = [];
      
      if (databaseManager && databaseManager.loadFileList) {
        files = await databaseManager.loadFileList();
      } else {
        // Fallback: fetch directly if DatabaseManager not available
        const baseUrl = apiBase || resolveApiBase();
        const params = new URLSearchParams();
        if (documentId) params.append("document_id", documentId);
        const res = await request(`${baseUrl}/db/files?${params.toString()}`, { method: "GET" });
        if (!res.ok) throw new Error(`Failed to fetch database files (${res.status})`);
        const data = await res.json();
        files = Array.isArray(data?.files) ? data.files : [];
      }
      
      if (!files.length) {
        console.error("[ask_user] No files found in the current database");
        return;
      }
  
      // 2) Show file picker dialog
      const chosenId = await showFilePickerDialog(files, selectedFileId || null);
      if (!chosenId) return; // user cancelled
  
      // 3) Update local state + chip, then auto-submit
      const file = files.find(f => String(f.file_id) === String(chosenId));
      selectedFileId = String(chosenId);
      // Show relative path to display folder hierarchy
      const basePath = getCommonBasePath(files);
      selectedFileLabel = file?.path ? getRelativePath(file.path, basePath) : (file?.title || `File ${chosenId}`);
      mode = "file";
  
      markActive(null);
      showChip(selectedFileLabel);
      
      await submit("picked-file"); // Sends FILE_ID:<id> [+ note]
    } catch (e) {
      console.error("[ask_user] File picking failed:", e);
      const statusEl = document.getElementById('status');
      if (statusEl) {
        statusEl.textContent = `Failed to load files: ${e.message}`;
      }
    }
  });
  
  // --- No file in DB ---
  noFileBtn.addEventListener("click", async (e) => {
    console.log('[ask_user] No file button clicked', e);
    selectedFileId = null;
    selectedFileLabel = null;
    hideChip();
    mode = "no_file";
    markActive(noFileBtn);
    await submit("no-file");
  });
  
  // --- Go online ---
  goOnlineBtn.addEventListener("click", async (e) => {
    console.log('[ask_user] Go online button clicked', e);
    selectedFileId = null;
    selectedFileLabel = null;
    hideChip();
    mode = "go_online";
    markActive(goOnlineBtn);
    await submit("go-online");
  });

  chipClear.addEventListener('click', () => {
    selectedFileId = null;
    selectedFileLabel = null;
    mode = null;
    hideChip();
  });

  setPauseSelection(true);
  freeInput.addEventListener('focusin', () => setPauseSelection(true));
  freeInput.addEventListener('focus', () => setPauseSelection(true));
  freeInput.addEventListener('blur', () => setTimeout(() => setPauseSelection(false), 150));

  const shownAt = Date.now();

  const buildAnswer = () => {
    const note = (freeInput.value || '').trim();
    if (mode === 'file' && selectedFileId) {
      return `FILE_ID:${selectedFileId}${note ? ' ' + note : ''}`;
    }
    if (mode === 'no_file') {
      return `NO_FILE${note ? ' ' + note : ''}`;
    }
    if (mode === 'go_online') {
      return `GO_ONLINE${note ? ' ' + note : ''}`;
    }
    return note || 'N/A';
  };

  const submit = async (origin) => {
    console.log('[ask_user] submit called with origin:', origin);
    const elapsedOk = (Date.now() - shownAt) >= 300; // tiny guard against accidental instant submits
    const answer = buildAnswer();
    console.log('[ask_user] submit - answer:', answer, 'elapsedOk:', elapsedOk, 'mode:', mode);
    // Allow submission for explicit actions (no-file, go-online, picked-file) immediately
    const isExplicitAction = ['no-file', 'go-online', 'picked-file'].includes(origin);
    if (!answer && origin !== 'cancel' && origin !== 'escape' && !elapsedOk && !isExplicitAction) {
      console.log('[ask_user] Blocking submit - no answer and conditions not met');
      return;
    }

    // Disable all
    [freeInput, ok, cancel, pickBtn, noFileBtn, goOnlineBtn, chipClear].forEach(el => { if (el) el.disabled = true; });

    try {
      const baseUrl = apiBase || await resolveApiBase();
      console.log('[ask_user] Making reply request to:', `${baseUrl}/reply`);
      console.log('[ask_user] Request payload:', {
        question_id: qid,
        answer,
        document_id: documentId,
        client_id: clientId,
        footnote_id: String(footnoteId ?? ''),
        cite_id: String(citeId ?? '')
      });
      
      const response = await request(`${baseUrl}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Backend ignores extra fields; they help on the orchestrator side.
        body: JSON.stringify({
          question_id: qid,
          answer,
          document_id: documentId,
          client_id: clientId,
          footnote_id: String(footnoteId ?? ''),
          cite_id: String(citeId ?? '')
        }),
      });
      console.log('[ask_user] Reply response received:', response.status, response.statusText);
      
      // 404: the agent stopped waiting for this question. There is nothing left
      // to answer, so drop the card instead of re-enabling it.
      if (response.status === 404) {
        console.warn('[ask_user] Question expired on the server, removing card:', qid);
        retractInlineAsk(qid);
        return;
      }

      // Check if response is OK
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unable to read error response');
        console.error('[ask_user] Reply failed with status:', response.status, 'Error:', errorText);
        throw new Error(`Reply failed: ${response.status} ${errorText}`);
      }
      
      const responseData = await response.json().catch(() => ({}));
      console.log('[ask_user] Reply response data:', responseData);
      ask.classList.add('submitted');
      
      // Check if we're in verify-all overlay (don't scroll in that context)
      const overlay = document.getElementById('verify-all-overlay')?.__api;
      const isInVerifyAll = !!(overlay && overlay.isVisible());
      
      // If NOT in verify-all, scroll to top of the card (like verify does)
      if (!isInVerifyAll && footnoteId) {
        // Find the card element by traversing up from the ask element
        let card = ask.closest('.card');
        if (card) {
          // Use the same scroll behavior as verifyFootnote
          card.scrollIntoView({ behavior: 'smooth', block: 'start' });
          console.log('[ask_user] Scrolled to top of card after resolving ask_user question');
        }
      }
      
      // Remove the ask element from DOM after successful submission
      setTimeout(() => {
        if (ask && ask.parentNode) {
          console.log('[ask_user] Removing ask element from DOM');
          ask.remove();
          
          // If this was in the verify-all overlay, notify it
          if (isInVerifyAll) {
            overlay.removeResolution(qid);
          }
        }
      }, 300); // Shorter delay - element fades out quickly
    } catch (e) {
      console.error("[ask_user] Reply error:", e);
      console.error("[ask_user] Error details:", {
        name: e.name,
        message: e.message,
        stack: e.stack
      });
      
      // Show error to user
      const statusEl = document.getElementById('status');
      if (statusEl) {
        statusEl.textContent = `Failed to send reply: ${e.message || e}`;
      }
      
      // Re-enable in case of failure
      [freeInput, ok, cancel, pickBtn, noFileBtn, goOnlineBtn, chipClear].forEach(el => { if (el) el.disabled = false; });
      return;
    } finally {
      setPauseSelection(false);
    }
    
    // For cancel/dismiss actions, also remove the ask element
    if (origin === 'cancel' || origin === 'escape') {
      setTimeout(() => {
        if (ask && ask.parentNode) {
          ask.remove();
        }
      }, 100); // Quick removal for cancel actions
    }
  };

  ok.onclick = () => submit('ok');
  cancel.onclick = () => { resetModes(); freeInput.value = ''; submit('cancel'); };
  freeInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') submit('enter');
    else if (e.key === 'Escape') submit('escape');
  });

  container.prepend(ask);
  
  setTimeout(() => freeInput.focus(), 0);
}